// components/popup/AddHigherCourtPopup.tsx
import React, { useState, useEffect } from "react";
import { BasePopup } from "./BasePopup";
import { useTooltip } from "context/TooltipProvider";
import {
  updateAssetAppeal,
  updateRespondentAssetAppeal,
} from "services/assetAppealService";
import { AssetAppeal } from "types";
import { MultipleFileUploadComponent } from "components/form/MultipleFileUpload";

interface AddHigherCourtPopupProps {
  isOpen: boolean;
  onClose: () => void;
  appealId: string;
  appealType: string;
  existingData?: {
    higher_court_name?: string | null;
    higher_court_type?: string | null;
    higher_court_level?: string | null;
    higher_court_hearing_date?: string | null;
    registration_number?: string | null;
    registration_date?: string | null;
    court_appeal_number?: string | null;
    court_summons?: string | null;
    opening_date?: string | null;
    first_hearidng_date?: string | null;
  };
  onSuccess?: React.Dispatch<React.SetStateAction<AssetAppeal | null>>;
  setActiveTab?: React.Dispatch<React.SetStateAction<string>>;
}

export const AddAppealHigherCourtPopup: React.FC<AddHigherCourtPopupProps> = ({
  isOpen,
  onClose,
  appealId,
  existingData,
  onSuccess,
  setActiveTab,
  appealType,
}) => {
  const [formData, setFormData] = useState({
    higher_court_name: "",
    higher_court_type: "",
    higher_court_level: "",
    higher_court_hearing_date: "",
    registration_number: "",
    registration_date: "",
    court_appeal_number: "",
    court_summons: "",
    opening_date: "",
    first_hearidng_date: "",
  });

  const [loading, setLoading] = useState(false);
  const { showTooltip } = useTooltip();
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  // Pre-fill form with existing data
  useEffect(() => {
    if (existingData && isOpen) {
      setFormData({
        higher_court_name: existingData.higher_court_name || "",
        higher_court_type: existingData.higher_court_type || "",
        higher_court_level: existingData.higher_court_level || "",
        higher_court_hearing_date: existingData.higher_court_hearing_date || "",
        registration_number: existingData.registration_number || "",
        registration_date: existingData.registration_date || "",
        court_appeal_number: existingData.court_appeal_number || "",
        court_summons: existingData.court_summons || "",
        opening_date: existingData.opening_date || "",
        first_hearidng_date: existingData.first_hearidng_date || "",
      });
    }
  }, [existingData, isOpen]);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateForm = () => {
    if (!formData.higher_court_name) return "Higher Court Name is required.";
    if (!formData.higher_court_type) return "Higher Court Type is required.";
    if (!formData.higher_court_level) return "Higher Court Level is required.";
    if (!formData.higher_court_hearing_date)
      return "Higher Court Hearing Date is required.";
    if (!formData.registration_number)
      return "Registration Number is required.";
    if (!formData.registration_date) return "Registration Date is required.";
    if (!formData.court_appeal_number)
      return "Court Appeal Number is required.";
    if (!formData.opening_date) return "Opening Date is required.";
    if (!formData.first_hearidng_date) return "First Hearing Date is required.";
    return "";
  };

  const handleClose = () => {
    // Reset form when closing
    setFormData({
      higher_court_name: "",
      higher_court_type: "",
      higher_court_level: "",
      higher_court_hearing_date: "",
      registration_number: "",
      registration_date: "",
      court_appeal_number: "",
      court_summons: "",
      opening_date: "",
      first_hearidng_date: "",
    });
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    const validationError = validateForm();
    if (validationError) {
      showTooltip(validationError, "error");
      return;
    }

    try {
      setLoading(true);

      // Create FormData for the request
      const submitFormData = new FormData();

      // Append all form fields
      Object.keys(formData).forEach((key) => {
        if (formData[key as keyof typeof formData]) {
          submitFormData.append(key, formData[key as keyof typeof formData]);
        }
      });
      if (uploadedFiles.length > 0) {
        uploadedFiles?.forEach((file) => {
          submitFormData.append("appeal_summon_files", file);
        });
      }
      let response;
      if (appealType === "appeal") {
        response = await updateAssetAppeal(appealId, submitFormData);
      } else {
        response = await updateRespondentAssetAppeal(appealId, submitFormData);
      }
      if (response) {
        // Show success message
        showTooltip("Higher court details added successfully!", "success");

        // Reset form and close popup
        handleClose();
      }

      // Call success callback if provided
      if (onSuccess) {
        if (appealType === "appeal") {
          onSuccess(response?.appeal);
        } else {
          onSuccess(response);
        }
        setActiveTab?.("Appeal");
      }
    } catch (error: any) {
      console.error("Error adding higher court details:", error);
      const errorMessage =
        error.response?.data?.message || "Failed to add higher court details";
      showTooltip(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <BasePopup
      isOpen={isOpen}
      onClose={handleClose}
      title="Add Higher Court Details"
      description="Add higher court information for the appeal"
    >
      <form onSubmit={handleSubmit}>
        {/* Higher Court Name */}
        <div className="flex flex-col mt-4">
          <label className="referral-popup-label">Higher Court Name</label>
          <input
            type="text"
            className="referral-form-field"
            onChange={handleInputChange}
            name="higher_court_name"
            placeholder="Enter higher court name"
            value={formData.higher_court_name}
          />
        </div>

        {/* Higher Court Type */}
        <div className="flex flex-col mt-4">
          <label className="referral-popup-label">Higher Court Type</label>
          <input
            type="text"
            className="referral-form-field"
            onChange={handleInputChange}
            name="higher_court_type"
            placeholder="Enter court type"
            value={formData.higher_court_type}
          />
        </div>

        {/* Higher Court Level */}
        <div className="flex flex-col mt-4">
          <label className="referral-popup-label">Higher Court Level</label>
          <input
            type="text"
            className="referral-form-field"
            onChange={handleInputChange}
            name="higher_court_level"
            placeholder="Enter court level"
            value={formData.higher_court_level}
          />
        </div>

        {/* Higher Court Hearing Date */}
        <div className="flex flex-col mt-4">
          <label className="referral-popup-label">
            Higher Court Hearing Date
          </label>
          <input
            type="date"
            className="referral-form-field"
            onChange={handleInputChange}
            name="higher_court_hearing_date"
            value={formData.higher_court_hearing_date}
          />
        </div>

        {/* Registration Number */}
        <div className="flex flex-col mt-4">
          <label className="referral-popup-label">Registration Number</label>
          <input
            type="text"
            className="referral-form-field"
            onChange={handleInputChange}
            name="registration_number"
            placeholder="Enter registration number"
            value={formData.registration_number}
          />
        </div>

        {/* Registration Date */}
        <div className="flex flex-col mt-4">
          <label className="referral-popup-label">Registration Date</label>
          <input
            type="date"
            className="referral-form-field"
            onChange={handleInputChange}
            name="registration_date"
            value={formData.registration_date}
          />
        </div>

        {/* Court Appeal Number */}
        <div className="flex flex-col mt-4">
          <label className="referral-popup-label">Court Appeal Number</label>
          <input
            type="text"
            className="referral-form-field"
            onChange={handleInputChange}
            name="court_appeal_number"
            placeholder="Enter court appeal number"
            value={formData.court_appeal_number}
          />
        </div>

        {/* Court Summons */}
        <div className="flex flex-col mt-4">
          <MultipleFileUploadComponent
            uploadedFiles={uploadedFiles}
            onFilesChange={setUploadedFiles}
            label="Court Summons"
          />
        </div>

        {/* Opening Date */}
        <div className="flex flex-col mt-4">
          <label className="referral-popup-label">Opening Date</label>
          <input
            type="date"
            className="referral-form-field"
            onChange={handleInputChange}
            name="opening_date"
            value={formData.opening_date}
          />
        </div>

        {/* First Hearing Date */}
        <div className="flex flex-col mt-4">
          <label className="referral-popup-label">First Hearing Date</label>
          <input
            type="date"
            className="referral-form-field"
            onChange={handleInputChange}
            name="first_hearidng_date"
            value={formData.first_hearidng_date}
          />
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={loading}
          className={`mt-6 mb-4 w-full referral-form-button ${
            loading ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-800"
          }`}
        >
          {loading ? "Submitting..." : "Add Higher Court Details"}
        </button>
      </form>
    </BasePopup>
  );
};
